import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PawPrint, Heart, Bone } from "./Doodles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "border-2 border-[#16294B] bg-[#FBF3E3] text-[#16294B] placeholder:text-[#4A5568]/60 shadow-[3px_3px_0_0_#16294B] focus-visible:ring-[#3B82F6] focus-visible:ring-offset-0 rounded-lg h-11";

const Forms = () => {
  const [volunteer, setVolunteer] = useState({ name: "", email: "", phone: "", interest: "build-days", message: "" });
  const [contact, setContact] = useState({ name: "", email: "", subject: "", message: "" });
  const [busyV, setBusyV] = useState(false);
  const [busyC, setBusyC] = useState(false);

  const submitVolunteer = async (e) => {
    e.preventDefault();
    setBusyV(true);
    try {
      const res = await axios.post(`${API}/volunteers`, volunteer);
      toast.success(res.data.message || "You're in! See you at the next build day.");
      setVolunteer({ name: "", email: "", phone: "", interest: "build-days", message: "" });
    } catch {
      toast.error("Something chewed through the wires — please try again.");
    } finally {
      setBusyV(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setBusyC(true);
    try {
      const res = await axios.post(`${API}/contact`, contact);
      toast.success(res.data.message || "Message sent! We'll fetch a reply soon.");
      setContact({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something chewed through the wires — please try again.");
    } finally {
      setBusyC(false);
    }
  };

  return (
    <section id="join" data-testid="join-section" className="relative scroll-mt-24 border-t-2 border-[#16294B] bg-[#F4E9D2] py-24 md:py-32" aria-label="Volunteer with Project Archie or contact us">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-14 text-center"
        >
          <div className="flex items-center justify-center gap-4">
            <Bone className="h-8 w-8 -rotate-12" color="#16294B" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#E63946]">Get involved</span>
            <Bone className="h-8 w-8 rotate-12" color="#16294B" />
          </div>
          <h2 className="mx-auto mt-4 max-w-2xl font-display text-5xl leading-tight text-[#16294B] md:text-7xl">
            Grab a drill. <span className="hl-underline">Join the pack</span>.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base text-[#4A5568] md:text-lg">
            Volunteer at a build day, or reach out about shelter partnerships, donations, and materials.
          </p>
        </motion.div>

        <div className="grid gap-10 lg:grid-cols-2">
          <motion.form
            data-testid="volunteer-form"
            onSubmit={submitVolunteer}
            initial={{ opacity: 0, y: 40, rotate: -1 }}
            whileInView={{ opacity: 1, y: 0, rotate: -1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="relative border-2 border-[#16294B] bg-[#FBF3E3] p-7 shadow-[8px_8px_0_0_#16294B] md:p-9"
          >
            <span className="absolute -top-5 -right-3 rotate-12">
              <PawPrint className="h-11 w-11" color="#3B82F6" />
            </span>
            <h3 className="font-display text-3xl text-[#16294B]">Volunteer sign-up</h3>
            <p className="mt-1 text-sm text-[#4A5568]">No experience needed — we teach you everything on the day.</p>

            <div className="mt-6 grid gap-4">
              <Input data-testid="volunteer-name-input" required placeholder="Your name" value={volunteer.name} onChange={(e) => setVolunteer({ ...volunteer, name: e.target.value })} className={inputCls} aria-label="Your name" />
              <div className="grid gap-4 sm:grid-cols-2">
                <Input data-testid="volunteer-email-input" required type="email" placeholder="Email" value={volunteer.email} onChange={(e) => setVolunteer({ ...volunteer, email: e.target.value })} className={inputCls} aria-label="Your email" />
                <Input data-testid="volunteer-phone-input" type="tel" placeholder="Phone (optional)" value={volunteer.phone} onChange={(e) => setVolunteer({ ...volunteer, phone: e.target.value })} className={inputCls} aria-label="Your phone number" />
              </div>
              <select
                data-testid="volunteer-interest-select"
                value={volunteer.interest}
                onChange={(e) => setVolunteer({ ...volunteer, interest: e.target.value })}
                className={`${inputCls} w-full px-3 text-sm font-medium`}
                aria-label="What would you like to help with?"
              >
                <option value="build-days">Build days (drills, timber, paint)</option>
                <option value="treasury">Treasury &amp; fundraising</option>
                <option value="anything">Happy to help anywhere</option>
              </select>
              <Textarea data-testid="volunteer-message-input" placeholder="Anything we should know? (skills, school, questions...)" value={volunteer.message} onChange={(e) => setVolunteer({ ...volunteer, message: e.target.value })} className={`${inputCls} h-24 resize-none py-2`} aria-label="Message for the volunteer team" />
              <button
                data-testid="volunteer-submit-btn"
                type="submit"
                disabled={busyV}
                className="btn-press mt-2 rounded-full border-2 border-[#16294B] bg-[#E63946] px-8 py-3.5 text-sm font-bold uppercase tracking-widest text-[#FBF3E3] shadow-[5px_5px_0_0_#16294B] disabled:opacity-60"
              >
                {busyV ? "Signing you up..." : "Count me in"}
              </button>
            </div>
          </motion.form>

          <motion.form
            data-testid="contact-form"
            onSubmit={submitContact}
            initial={{ opacity: 0, y: 40, rotate: 1 }}
            whileInView={{ opacity: 1, y: 0, rotate: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative border-2 border-[#16294B] bg-[#FBF3E3] p-7 shadow-[8px_8px_0_0_#3B82F6] md:p-9"
          >
            <span className="absolute -top-5 -left-3 -rotate-12">
              <Heart className="h-10 w-10" />
            </span>
            <h3 className="font-display text-3xl text-[#16294B]">Questions? Reach out!</h3>
            <p className="mt-1 text-sm text-[#4A5568]">
              If you have any questions, feel free to reach out at{" "}
              <a data-testid="contact-email-link" href="mailto:projectarchiee@gmail.com" className="font-bold text-[#16294B] underline decoration-[#E63946] decoration-2 underline-offset-2 transition-colors hover:text-[#E63946]">
                projectarchiee@gmail.com
              </a>{" "}
              — or send us a message right here:
            </p>

            <div className="mt-6 grid gap-4">
              <Input data-testid="contact-name-input" required placeholder="Your name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className={inputCls} aria-label="Your name" />
              <Input data-testid="contact-email-input" required type="email" placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className={inputCls} aria-label="Your email" />
              <Input data-testid="contact-subject-input" placeholder="Subject (shelter partnership, materials donation...)" value={contact.subject} onChange={(e) => setContact({ ...contact, subject: e.target.value })} className={inputCls} aria-label="Message subject" />
              <Textarea data-testid="contact-message-input" required placeholder="Your message" value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} className={`${inputCls} h-32 resize-none py-2`} aria-label="Your message" />
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={busyC}
                className="btn-press mt-2 rounded-full border-2 border-[#16294B] bg-[#3B82F6] px-8 py-3.5 text-sm font-bold uppercase tracking-widest text-[#FBF3E3] shadow-[5px_5px_0_0_#16294B] disabled:opacity-60"
              >
                {busyC ? "Sending..." : "Send message"}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Forms;
